import { z } from 'zod';

export const zodMakeFilteredArraySchema = <T extends z.ZodSchema>(schema: T) =>
  z
    .array(z.unknown())
    .transform((items) =>
      items.filter(
        (item): item is z.infer<T> => schema.safeParse(item).success,
      ),
    );

export const commonQueryParamsSchema = z.object({
  categoryId: z.string().optional(),
  specialtyId: z.string().optional(),
});
export type CommonQueryParams = z.infer<typeof commonQueryParamsSchema>;

export const recordStatusSchema = z.enum(['C', 'U', 'D', 'R']);

// reference: https://github.com/colinhacks/zod/discussions/863#discussioncomment-15479465
export type ZodNonNullable<Schema extends z.ZodType> =
  Schema extends z.ZodNullable<infer T extends z.ZodType>
    ? ZodNonNullable<T>
    : Schema;

export const nonnullable = <Schema extends z.ZodType>(
  schema: Schema,
): ZodNonNullable<Schema> => {
  let unwrapped: z.ZodType = schema;
  while (unwrapped instanceof z.ZodNullable) {
    unwrapped = unwrapped.unwrap() as z.ZodType;
  }
  return unwrapped as ZodNonNullable<Schema>;
};

export type ZodRemoveNullable<
  Schema extends z.ZodObject,
  Mask extends keyof Schema['shape'],
> = z.ZodObject<{
  [key in keyof Schema['shape']]: key extends Mask
    ? ZodNonNullable<Schema['shape'][key]>
    : Schema['shape'][key];
}>;

export type ZodRemoveNullableExcept<
  Schema extends z.ZodObject,
  Mask extends keyof Schema['shape'],
> = z.ZodObject<{
  [key in keyof Schema['shape']]: key extends Mask
    ? Schema['shape'][key]
    : ZodNonNullable<Schema['shape'][key]>;
}>;

/**
 * @description z.number().nullable() to z.number()
 * @param includeMask - only remove nullable from these keys
 * @param excludeMask - remove nullable from all keys except these
 */
export function zodRemoveNullable<
  Schema extends z.ZodObject,
  K extends keyof Schema['shape'],
>(
  schema: Schema,
  options: { excludeMask: { [key in K]: true } },
): ZodRemoveNullableExcept<Schema, K>;
export function zodRemoveNullable<
  Schema extends z.ZodObject,
  K extends keyof Schema['shape'],
>(
  schema: Schema,
  options?: { includeMask: { [key in K]: true } },
): ZodRemoveNullable<Schema, K>;
export function zodRemoveNullable<
  Schema extends z.ZodObject,
  K extends keyof Schema['shape'],
>(
  schema: Schema,
  options?:
    | { excludeMask: { [key in K]: true }; includeMask?: never }
    | { includeMask: { [key in K]: true }; excludeMask?: never },
) {
  const entries = Object.entries(schema.shape) as [K, z.ZodType][];
  const newProps = entries.reduce(
    (newProps, [key, value]) => {
      const shouldRemove = (() => {
        if (!options) {
          return true;
        }

        if ('excludeMask' in options && options.excludeMask) {
          return !options.excludeMask[key];
        }

        if ('includeMask' in options && options.includeMask) {
          return options.includeMask[key];
        }
      })();
      newProps[key] = shouldRemove ? nonnullable(value) : value;
      return newProps;
    },
    {} as Record<K, z.ZodType>,
  );
  return z.object(newProps);
}

export const zodTransformNullDefaultArray = <T extends null | unknown[]>(
  val: T,
) => (val === null ? [] : val);

export const zodTransformEmptyStringToNull = (val: string) =>
  val === '' ? null : val;
